/**
 * Which collection holds this work, and the two acts that change it — OND-22.
 *
 * A SELECT AND A BUTTON FOR THE MOVE, A BUTTON ASKED TWICE FOR THE REMOVAL. The
 * two acts are drawn as differently as they are different: moving a work between
 * your own collections changes who may read nothing, and is one gesture;
 * removing it is the one act on this record after which you may not be able to
 * open it again, so the first press turns the button into a sentence saying so,
 * and only the second press does anything. Whether the panel is armed is the
 * controller's state, not this component's, so a test can assert that one press
 * removes nothing without rendering a browser.
 *
 * It renders nothing at all for anybody but the owner of the holding
 * collection, because the model hands it null: `ondo.move_work()` and
 * `ondo.remove_work_from_collection()` both ask `owns_collection`, and a panel
 * drawn disabled for a curator would be a control the database will refuse.
 *
 * The refusal is PRINTED, unlike the visibility panel's, because one of them is
 * worth reading: "this work has been sold and is awaiting its buyer" is the
 * database telling the owner why nothing moved (OND-17).
 */
import { useId, useState } from "react";

import { WORK_PLACEMENT_COPY } from "./work.constants.ts";
import type { PlacementPanel } from "./work.model.ts";

import "./work.css";

type Props = {
  panel: PlacementPanel | null;
  busy: boolean;
  failed: string | null;
  onMove: (collectionId: string) => void;
  onArmRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
};

export function WorkPlacementPanel({
  panel,
  busy,
  failed,
  onMove,
  onArmRemove,
  onCancelRemove,
  onRemove,
}: Props) {
  const headingId = useId();
  const selectId = useId();
  // Which destination is highlighted. Form state and nothing more: it decides
  // nothing until Move is pressed, so it is the one piece of state this view is
  // allowed to hold.
  const [chosen, setChosen] = useState("");

  if (panel === null) return null;

  const target = panel.targets.find((candidate) => candidate.id === chosen) ?? panel.targets[0];

  return (
    <section className="work__panel work-placement" aria-labelledby={headingId}>
      <h3 className="work__panel-head" id={headingId}>
        {WORK_PLACEMENT_COPY.heading}
      </h3>
      <p className="work-placement__lead">{WORK_PLACEMENT_COPY.lead}</p>

      <p className="work-placement__current">
        <span className="work-placement__current-label">{WORK_PLACEMENT_COPY.currently}</span>{" "}
        <strong>{panel.current.name}</strong>
      </p>

      {panel.loadingTargets ? (
        <p className="work-placement__note" role="status">
          {WORK_PLACEMENT_COPY.loadingTargets}
        </p>
      ) : panel.targets.length === 0 ? (
        <p className="work-placement__note">{WORK_PLACEMENT_COPY.noOtherCollections}</p>
      ) : (
        <div className="work-placement__move">
          <label className="work-placement__move-label" htmlFor={selectId}>
            {WORK_PLACEMENT_COPY.moveLabel}
          </label>
          <select
            id={selectId}
            className="work-placement__select"
            value={target?.id ?? ""}
            disabled={busy}
            onChange={(event) => setChosen(event.target.value)}
          >
            {panel.targets.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="work-placement__action"
            disabled={busy || target === undefined}
            onClick={() => {
              if (target !== undefined) onMove(target.id);
            }}
          >
            {WORK_PLACEMENT_COPY.moveAction}
          </button>
        </div>
      )}

      {/* THE SAFETY CATCH. One button until it is pressed; then the sentence
          and two buttons, of which only the first does anything. The sentence
          says what is kept as well as what is lost, because "remove" beside a
          work's record reads as deletion and the database does no such thing. */}
      {panel.removeArmed ? (
        <div className="work-placement__remove work-placement__remove--armed" role="group">
          <p className="work-placement__warning">{WORK_PLACEMENT_COPY.removeArmedLead}</p>
          <div className="work-placement__remove-actions">
            <button
              type="button"
              className="work-placement__action work-placement__action--danger"
              disabled={busy}
              onClick={onRemove}
            >
              {WORK_PLACEMENT_COPY.removeConfirm}
            </button>
            <button
              type="button"
              className="work-placement__action"
              disabled={busy}
              onClick={onCancelRemove}
            >
              {WORK_PLACEMENT_COPY.removeCancel}
            </button>
          </div>
        </div>
      ) : (
        <div className="work-placement__remove">
          <button
            type="button"
            className="work-placement__action work-placement__action--quiet"
            disabled={busy}
            onClick={onArmRemove}
          >
            {WORK_PLACEMENT_COPY.removeAction}
          </button>
        </div>
      )}

      {busy && (
        <p className="work-placement__state" role="status">
          {WORK_PLACEMENT_COPY.saving}
        </p>
      )}
      {failed !== null && !busy && (
        <p className="work-placement__state work-placement__state--failed" role="alert">
          {failed}
        </p>
      )}
    </section>
  );
}
